<template>
  <div class="vdo-migration">
    <vx-card :title="$t('vdo-migration.title')">
      <div class="flex items-center justify-between mb-4">
        <span class="text-sm">
          {{ $t('vdo-migration.fetched-at') }}: {{ local.fetchedAt || '-' }}
        </span>
        <vs-button size="small" @click="refresh">{{ $t('vdo-migration.refresh') }}</vs-button>
      </div>

      <!-- Never rendered as an empty table. A failed fetch and a tenant with
           no data must not look alike -- that confusion cost an afternoon. -->
      <vs-alert v-if="local.loadFailed" color="danger" class="mb-4">
        {{ $t('vdo-migration.load-failed') }}
      </vs-alert>

      <vs-alert v-if="dashboard && !dashboard.config" color="warning" class="mb-4">
        {{ $t('vdo-migration.config.not-seeded') }}
      </vs-alert>

      <div v-if="dashboard && dashboard.config" class="mb-6">
        <span class="mr-6">{{ $t('vdo-migration.config.enabled') }}: <b>{{ dashboard.config.enabled }}</b></span>
        <span class="mr-6">{{ $t('vdo-migration.config.cap') }}: <b>{{ dashboard.config.inflightCap }}</b></span>
        <span>{{ $t('vdo-migration.config.updated-at') }}: {{ formatTime(dashboard.config.updatedAt) }} ({{ dashboard.config.updatedBy }})</span>
      </div>

      <h5 class="mb-2">{{ $t('vdo-migration.control.title') }}</h5>
      <!-- Writes are refused entirely while this is false: the audit row
           shares the change's transaction on purpose, so a schema without the
           table cannot record -- and therefore cannot act. -->
      <vs-alert v-if="dashboard && dashboard.auditLogAvailable === false" color="warning" class="mb-4">
        {{ $t('vdo-migration.control.unavailable') }}
      </vs-alert>
      <div v-else-if="dashboard && dashboard.config" class="flex flex-wrap items-center mb-6">
        <vs-button
          v-if="!dashboard.config.enabled"
          class="mr-2"
          size="small"
          @click="confirmEnable">{{ $t('vdo-migration.control.enable') }}</vs-button>
        <vs-button
          v-else
          class="mr-2"
          size="small"
          color="warning"
          @click="confirmDisable">{{ $t('vdo-migration.control.disable') }}</vs-button>

        <span class="mr-1">{{ $t('vdo-migration.control.cap') }}</span>
        <vs-input
          v-model="local.cap"
          class="mr-2"
          type="number"
          min="1"
          :max="MAX_CAP"
          @focus="capFocused = true"
          @blur="capFocused = false"/>
        <vs-button class="mr-4" size="small" @click="confirmCap">{{ $t('vdo-migration.control.apply') }}</vs-button>

        <!-- Disabled while a preview is pending: the confirm below can only
             ever apply to the reason that produced it (see previewedReason),
             so letting the operator move the select and believe that reason
             is what gets confirmed would be actively misleading. -->
        <vs-select v-model="local.requeueReason" class="mr-2" :disabled="!!requeuePreview">
          <vs-select-item :value="''" :text="$t('vdo-migration.control.reason-all')"/>
          <vs-select-item v-for="reason in failureReasons" :key="reason" :value="reason" :text="reason"/>
        </vs-select>
        <vs-button size="small" color="danger" @click="confirmStartRequeue">{{ $t('vdo-migration.control.requeue') }}</vs-button>
      </div>

      <!-- The list the server refused on. It is already recorded on the audit
           row, so losing this screen no longer loses the only pointer to a
           Bunny video that keeps billing -- but a human still has to delete
           them by hand. -->
      <vs-alert v-if="requeuePreview" color="danger" class="mb-4">
        <p class="mb-2">{{ $t('vdo-migration.control.orphan-warning') }}</p>
        <p class="mb-2">{{ $t('vdo-migration.control.previewed-reason', { reason: previewedReasonLabel }) }}</p>
        <ul class="mb-2">
          <li v-for="row in requeuePreview.orphaned" :key="row.jobId">
            {{ row.vdoSetTopicId }} — {{ row.bunnyVideoGuid }}
          </li>
        </ul>
        <vs-button size="small" color="danger" @click="confirmRequeue">{{ $t('vdo-migration.control.confirm-requeue') }}</vs-button>
      </vs-alert>

      <!-- Past tense, no confirm button, different color from the alert
           above: this requeue already ran. An operator must never mistake
           this completed to-do list for a pending confirmation -- it
           survives in the audit row's result JSON too, but reading raw JSON
           out of a table is not how this to-do list should have to be read. -->
      <vs-alert v-if="requeueCompleted" color="warning" class="mb-4">
        <p class="mb-2">{{ $t('vdo-migration.control.orphaned-after-requeue') }}</p>
        <p v-if="requeueCompleted.reason" class="mb-2">
          {{ $t('vdo-migration.control.previewed-reason', { reason: requeueCompleted.reason }) }}
        </p>
        <ul class="mb-2">
          <li v-for="row in requeueCompleted.orphaned" :key="row.jobId">
            {{ row.vdoSetTopicId }} — {{ row.bunnyVideoGuid }}
          </li>
        </ul>
      </vs-alert>

      <!-- `elsewhere`: the reason matched no FAILED row. Silence would read as
           "there is no work to do" when the truth is "this reason never
           fails" -- so an empty match is called out explicitly, and if the
           reason exists on other statuses, where. -->
      <vs-alert v-if="requeueElsewhere && requeueElsewhere.length === 0" color="warning" class="mb-4">
        {{ $t('vdo-migration.control.elsewhere-none') }}
      </vs-alert>
      <vs-alert v-else-if="requeueElsewhere && requeueElsewhere.length" color="warning" class="mb-4">
        <p class="mb-2">{{ $t('vdo-migration.control.elsewhere-warning') }}</p>
        <ul>
          <li v-for="row in requeueElsewhere" :key="row.status">{{ row.status }}: {{ row.count }}</li>
        </ul>
      </vs-alert>

      <h5 class="mb-2">{{ $t('vdo-migration.ticks.title') }}</h5>
      <!-- Distinct from "no ticks recorded yet": this tenant schema never had
           the hand-applied VdoMigrationTickLog migration run against it, so
           there is no table to have rows in the first place. -->
      <p v-if="dashboard && dashboard.tickLogAvailable === false">{{ $t('vdo-migration.ticks.table-not-created') }}</p>
      <p v-else-if="dashboard && !dashboard.ticks.length">{{ $t('vdo-migration.ticks.none') }}</p>
      <vs-table v-else :data="dashboard ? dashboard.ticks : []">
        <template slot="thead">
          <vs-th v-for="col in template.ticks.header" :key="col.id">{{ $t(col.title) }}</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr v-for="tick in data" :key="tick.id" :data="tick" :class="tickClass(tick)">
            <vs-td>{{ formatTime(tick.startedAt) }}</vs-td>
            <vs-td>{{ tick.durationMs }}</vs-td>
            <vs-td>{{ tick.outcome }}<span v-if="tick.dryRun"> (dry-run)</span></vs-td>
            <vs-td>{{ tick.lockRenewals }}</vs-td>
            <vs-td>{{ stopSignals(tick) || '-' }}</vs-td>
            <vs-td>{{ tick.pid }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <h5 class="mt-6 mb-2">{{ $t('vdo-migration.queue.title') }}</h5>
      <p v-if="dashboard && !dashboard.byStatus.length">{{ $t('vdo-migration.queue.none') }}</p>
      <vs-table v-else :data="dashboard ? dashboard.byStatus : []">
        <template slot="thead">
          <vs-th>{{ $t('vdo-migration.queue.status') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.queue.count') }}</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr v-for="row in data" :key="row.status" :data="row">
            <vs-td>{{ row.status }}</vs-td>
            <vs-td>{{ row.count }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <p v-if="dashboard && !dashboard.byReason.length" class="mt-4">{{ $t('vdo-migration.queue.none') }}</p>
      <vs-table v-else class="mt-4" :data="dashboard ? dashboard.byReason : []">
        <template slot="thead">
          <vs-th>{{ $t('vdo-migration.queue.status') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.queue.reason') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.queue.count') }}</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr v-for="row in data" :key="row.status + '-' + row.reason" :data="row">
            <vs-td>{{ row.status }}</vs-td>
            <vs-td>{{ row.reason }}</vs-td>
            <vs-td>{{ row.count }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <h5 class="mt-6 mb-2">{{ $t('vdo-migration.topic.title') }}</h5>
      <div class="flex items-center mb-2">
        <vs-input
          v-model="local.topicId"
          class="mr-2"
          :placeholder="$t('vdo-migration.topic.placeholder')"
          @keyup.enter="lookupTopic"/>
        <vs-button size="small" @click="lookupTopic">{{ $t('vdo-migration.topic.lookup') }}</vs-button>
      </div>

      <!-- Three distinct states, never one empty box: nothing looked up yet,
           looked up and not found, and found. -->
      <p v-if="!topic && !topicMissing">{{ $t('vdo-migration.topic.none') }}</p>
      <vs-alert v-else-if="topicMissing" color="warning" class="mb-4">
        {{ $t('vdo-migration.topic.not-found') }}
      </vs-alert>
      <div v-else>
        <vs-alert v-if="topic.divergent" color="danger" class="mb-2">
          {{ $t('vdo-migration.topic.divergent') }}
        </vs-alert>
        <div class="mb-1">
          <span class="mr-6">{{ $t('vdo-migration.topic.vdo-length') }}: <b>{{ topic.topic.vdoLength }}</b></span>
          <span class="mr-6">{{ $t('vdo-migration.topic.file-size') }}: {{ topic.topic.fileSize || '-' }}</span>
          <span class="mr-6">{{ $t('vdo-migration.topic.resolution') }}: {{ topic.topic.vdoResolution || '-' }}</span>
          <span class="mr-6">{{ $t('vdo-migration.topic.provider') }}: <b>{{ topic.topic.videoProvider }}</b></span>
        </div>
        <div class="mb-1">
          <span class="mr-6">{{ $t('vdo-migration.topic.guid') }}: {{ topic.topic.bunnyVideoGuid || '-' }}</span>
          <span class="mr-6">{{ $t('vdo-migration.topic.bunny-status') }}: {{ topic.topic.bunnyStatus }} ({{ formatTime(topic.topic.bunnyStatusAt) }})</span>
        </div>
        <div class="mb-1">{{ $t('vdo-migration.topic.stream-path') }}: {{ topic.topic.vdoStreamPath || '-' }}</div>
        <div v-if="topic.job">
          <span class="mr-6">{{ $t('vdo-migration.topic.job-status') }}: <b>{{ topic.job.status }}</b> {{ topic.job.reason || '' }}</span>
          <span class="mr-6">{{ $t('vdo-migration.topic.attempts') }}: {{ topic.job.attempts }}</span>
          <span>{{ $t('vdo-migration.topic.last-error') }}: {{ topic.job.lastError || '-' }}</span>
        </div>
      </div>

      <h5 class="mt-6 mb-2">{{ $t('vdo-migration.inflight.title') }}</h5>
      <p v-if="dashboard && !dashboard.inflight.length">{{ $t('vdo-migration.inflight.none') }}</p>
      <vs-table v-else :data="dashboard ? dashboard.inflight : []">
        <template slot="thead">
          <vs-th>{{ $t('vdo-migration.inflight.topic') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.inflight.guid') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.inflight.submitted-at') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.inflight.attempts') }}</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr v-for="job in data" :key="job.id" :data="job">
            <vs-td><a href="#" @click.prevent="showTopic(job.vdoSetTopicId)">{{ job.vdoSetTopicId }}</a></vs-td>
            <vs-td>{{ job.bunnyVideoGuid }}</vs-td>
            <vs-td>{{ formatTime(job.submittedAt) }}</vs-td>
            <vs-td>{{ job.attempts }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <h5 class="mt-6 mb-2">{{ $t('vdo-migration.failures.title') }}</h5>
      <p v-if="dashboard && !dashboard.recentFailures.length">{{ $t('vdo-migration.failures.none') }}</p>
      <vs-table v-else :data="dashboard ? dashboard.recentFailures : []">
        <template slot="thead">
          <vs-th>{{ $t('vdo-migration.failures.topic') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.failures.reason') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.failures.last-error') }}</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr v-for="job in data" :key="job.vdoSetTopicId" :data="job">
            <vs-td><a href="#" @click.prevent="showTopic(job.vdoSetTopicId)">{{ job.vdoSetTopicId }}</a></vs-td>
            <vs-td>{{ job.reason }}</vs-td>
            <vs-td>{{ job.lastError }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <h5 class="mt-6 mb-2">{{ $t('vdo-migration.audit.title') }}</h5>
      <p v-if="dashboard && dashboard.auditLogAvailable === false">{{ $t('vdo-migration.audit.table-not-created') }}</p>
      <p v-else-if="dashboard && !dashboard.audits.length">{{ $t('vdo-migration.audit.none') }}</p>
      <vs-table v-else :data="dashboard ? dashboard.audits : []">
        <template slot="thead">
          <vs-th>{{ $t('vdo-migration.audit.at') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.audit.actor') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.audit.source') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.audit.action') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.audit.params') }}</vs-th>
          <vs-th>{{ $t('vdo-migration.audit.result') }}</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr v-for="row in data" :key="row.id" :data="row" :class="row.ok ? '' : 'text-danger'">
            <vs-td>{{ formatTime(row.at) }}</vs-td>
            <vs-td>{{ row.actor }}</vs-td>
            <vs-td>{{ row.source }}</vs-td>
            <vs-td>{{ row.action }}</vs-td>
            <vs-td>{{ row.params }}</vs-td>
            <vs-td>{{ row.error || row.result }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>

    <loader v-if="local.isProcessing"/>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { confirmGeneral } from '@/utils/dialogs'
import { showStateNotification, success } from '@/utils/notification'
import pageData from './data'

const { mapActions } = createNamespacedHelpers('VdoMigration')

// A tick every 15 minutes; polling faster than this buys nothing.
const REFRESH_MS = 60 * 1000

// Mirrors MAX_INFLIGHT_CAP in the backend's vdo-migration-commands.js. The
// server refuses above it regardless; this only stops the field offering it.
const MAX_CAP = 50

export default {
  components: {
    Loader: () => import('@/components/common-ui/loader-block/index.vue')
  },
  data() {
    return {
      local: pageData.state.local,
      template: pageData.template,
      timer: null,
      MAX_CAP,
      // While true, refresh() must not overwrite local.cap: a poll landing
      // mid-keystroke must not fight the operator for what they are typing.
      capFocused: false,
    }
  },
  computed: {
    dashboard() {
      return this.$store.state.VdoMigration.dashboard
    },
    topic() {
      return this.$store.state.VdoMigration.topic
    },
    topicMissing() {
      return this.$store.state.VdoMigration.topicMissing
    },
    requeuePreview() {
      return this.$store.state.VdoMigration.requeuePreview
    },
    requeueElsewhere() {
      return this.$store.state.VdoMigration.requeueElsewhere
    },
    requeueCompleted() {
      return this.$store.state.VdoMigration.requeueCompleted
    },
    // What the confirm below is actually about to send -- shown next to the
    // orphan list so the operator is never in doubt about which reason they
    // are confirming, even though the select itself is disabled.
    previewedReasonLabel() {
      return this.local.previewedReason || this.$t('vdo-migration.control.reason-all')
    },
    // Derived from the data, never a hardcoded list. requeue only touches
    // FAILED rows, so a reason that no FAILED row carries would match nothing
    // and print "requeued 0" -- which reads exactly like "there is no work to
    // do". Several JOB_REASON values (FILE_NOT_FOUND, NO_STREAM_PATH,
    // ALREADY_BUNNY, DISABLED) belong to SKIPPED rows and must never be
    // offered here.
    failureReasons() {
      if (!this.dashboard) return []
      return this.dashboard.byReason
        .filter((row) => row.status === 'FAILED' && row.reason)
        .map((row) => row.reason)
    },
  },
  watch: {
    '$store.state.VdoMigration.processing'(value) {
      this.local.isProcessing = value
    },
    // Enable/disable/cap/requeue all commit setError on failure. Without this
    // the screen would refresh, look unchanged, and the operator would have
    // no way to tell the click did nothing.
    '$store.state.VdoMigration.error'(data) {
      showStateNotification(this.$vs, data)
    }
  },
  created() {
    this.refresh()
    this.timer = setInterval(this.refresh, REFRESH_MS)
  },
  beforeDestroy() {
    // Without this the poll keeps firing after the user navigates away, for
    // the rest of the session.
    clearInterval(this.timer)
  },
  methods: {
    ...mapActions(['getVdoMigrationDashboard', 'getVdoMigrationTopic', 'enableVdoMigration', 'disableVdoMigration', 'setVdoMigrationCap', 'requeueVdoMigrationJobs']),
    async refresh() {
      const result = await this.getVdoMigrationDashboard()
      this.local.loadFailed = result === null
      if (result !== null) {
        this.local.fetchedAt = new Date().toLocaleString()
        // Keep the cap field in step with the server on every poll, not just
        // at mount -- but never while the operator has focus in it, or a poll
        // landing mid-keystroke would revert what they are typing.
        if (!this.capFocused && result.config) {
          this.local.cap = result.config.inflightCap
        }
      }
    },
    async lookupTopic() {
      if (!this.local.topicId) return
      await this.getVdoMigrationTopic({ vdoSetTopicId: this.local.topicId.trim() })
    },
    showTopic(vdoSetTopicId) {
      this.local.topicId = vdoSetTopicId
      return this.lookupTopic()
    },
    confirmEnable() {
      confirmGeneral(this.$vs, async () => {
        await this.enableVdoMigration()
        await this.refresh()
      }, null, this.$t('vdo-migration.control.title'), this.$t('vdo-migration.control.confirm-enable'))
    },
    confirmDisable() {
      confirmGeneral(this.$vs, async () => {
        await this.disableVdoMigration()
        await this.refresh()
      }, null, this.$t('vdo-migration.control.title'), this.$t('vdo-migration.control.confirm-disable'))
    },
    confirmCap() {
      const cap = Number(this.local.cap)
      confirmGeneral(this.$vs, async () => {
        await this.setVdoMigrationCap({ cap })
        await this.refresh()
      }, null, this.$t('vdo-migration.control.title'), this.$t('vdo-migration.control.confirm-cap'))
    },
    confirmStartRequeue() {
      // Same guard as enable/disable/cap: the design spec says every action
      // goes through a confirmation, and the server does not ask for one
      // itself when no FAILED job carries a bunnyVideoGuid -- a single click
      // would otherwise reset every FAILED job in the tenant to PENDING with
      // no dialog anywhere in the flow.
      confirmGeneral(this.$vs, async () => {
        await this.startRequeue()
      }, null, this.$t('vdo-migration.control.title'), this.$t('vdo-migration.control.confirm-requeue-start'))
    },
    async startRequeue() {
      // First press: no `confirmed`. The server decides whether anything
      // would be orphaned, and its refusal is what records the guid list.
      const reason = this.local.requeueReason || null
      const result = await this.requeueVdoMigrationJobs({ reason })
      if (!result) return
      if (result.needsConfirmation) {
        // Snapshot the reason that produced this preview. confirmRequeue must
        // send exactly this, never a fresh read of local.requeueReason -- the
        // select can move on (or is disabled, but this is the value that
        // actually matters) while the preview is still on screen, and a
        // confirm has to bind to what the operator saw, not to whatever the
        // select holds by the time they click.
        this.local.previewedReason = reason
        return
      }
      this.local.previewedReason = null
      await this.refresh()
      this.notifyRequeued(result)
    },
    async confirmRequeue() {
      const result = await this.requeueVdoMigrationJobs({
        reason: this.local.previewedReason,
        confirmed: true
      })
      if (!result) return
      this.local.previewedReason = null
      await this.refresh()
      this.notifyRequeued(result)
    },
    notifyRequeued(result) {
      // A zero-match requeue is not a success to celebrate -- it is exactly
      // the case the `elsewhere` warning alert exists to explain, and a green
      // "Requeued 0" toast sitting right above that warning contradicts it.
      if (!result.requeued) return
      this.$vs.notify(success(
        'vdo-migration.control.title',
        this.$t('vdo-migration.control.requeued', { count: result.requeued })
      ))
    },
    formatTime(value) {
      // Every DateTime in this schema is a GraphQL String, and graphql-js
      // serializes a Date through String via valueOf() -- so what arrives is
      // "1787565600000", not an ISO string. new Date(value) on that is
      // Invalid Date; Number() first is what makes it a time.
      if (!value) return '-'
      const ms = Number(value)
      return Number.isNaN(ms) ? '-' : new Date(ms).toLocaleString()
    },
    stopSignals(tick) {
      // Same three signals, in the same order, as the tick's operator log
      // line. An operator reading both should not have to translate.
      return [
        tick.lockLost ? `LOCK-LOST=${tick.lockLost}` : null,
        tick.reconcileStoppedBy ? `RECONCILE-STOPPED=${tick.reconcileStoppedBy}` : null,
        tick.scanStoppedBy ? `CLAIM-STOPPED=${tick.scanStoppedBy}` : null
      ].filter(Boolean).join(' ')
    },
    tickClass(tick) {
      if (tick.lockLost) return 'text-danger'
      if (tick.lockRenewals > 0) return 'text-warning'
      return ''
    }
  }
}
</script>
